import { IMatch } from '@/models/match';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import React, { useEffect, useRef } from 'react';
import { LinkButton } from '../../components/link-button';
import { H4, P } from '../../components/text';

interface SquadMatchesListProps {
	matches?: IMatch[];
}

const SquadMatchesList: React.FC<SquadMatchesListProps> = ({ matches }) => {
	const observerRef = useRef<IntersectionObserver | null>(null);

	useEffect(() => {
		// Create the observer
		observerRef.current = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add('animate-fade-in-from-bottom');
						// Once animation is added, we can stop observing
						observerRef.current?.unobserve(entry.target);
					}
				});
			},
			{
				root: null,
				rootMargin: '0px',
				threshold: 0.1,
			},
		);

		// Observe all match cards
		const matchCards = document.querySelectorAll('.match-card');
		matchCards.forEach((card) => {
			observerRef.current?.observe(card);
		});

		// Cleanup
		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, [matches]);

	// Group matches by month
	const matchesGroupedByMonth: Record<string, IMatch[]> | undefined = matches?.reduce((acc, match) => {
		const monthKey = format(parseISO(match.date), 'MMM', { locale: nl });

		if (!acc[monthKey]) acc[monthKey] = [];
		acc[monthKey].push(match);

		return acc;
	}, {} as Record<string, IMatch[]>);

	if (!matchesGroupedByMonth || !Object.keys(matchesGroupedByMonth).length) {
		return <p className="mt-8">Geen wedstrijden gevonden</p>;
	}

	return Object.entries(matchesGroupedByMonth).map(([month, matches]) => (
		<section key={month} className="mb-6">
			<H4 className="mt-8 mb-6 uppercase">{month}</H4>
			<div className="space-y-4">
				{matches.map((match) => {
					const hasScores = match.scores?.away != null && match.scores?.home != null;

					const formatDate = (date: string) => format(parseISO(date), 'dd-MM-yyyy', { locale: nl });
					const stripCharactersFromSlug = (slug: string) => slug.replace('vs-', '').slice(0, -10);

					const pastMatchUrl = `/video/samenvatting-${stripCharactersFromSlug(match.slug)}-${formatDate(
						match.date,
					)}`;

					// Only show button if there are scores or if the match has a ticket URL
					const showButton =
						(hasScores && match.slug) || (!hasScores && match.homeContestant.contestantTicketURL);
					const buttonHref = (hasScores ? `${pastMatchUrl}` : match.homeContestant.contestantTicketURL) ?? '';
					const buttonText = hasScores ? 'Samenvatting' : 'koop kaarten';

					return (
						<article
							key={match.title}
							className="match-card grid grid-cols-[130px_1fr_200px] items-center bg-[#0C0C26] text-white border-b-4 border-red-500 h-[90px] opacity-0 translate-y-4"
						>
							{/* Left side */}
							<div className="py-6 px-5 flex flex-col">
								<P className="text-xs !text-[#D3D2DD] font-semibold !font-mono">
									{/* Slice first 5 chars to remove seconds from time string (e.g. "20:00:00" -> "20:00") */}
									<time dateTime={match.time}>{match.time.slice(0, 5)}</time>
								</P>
								<time
									dateTime={match.date}
									className="text-lg text-white uppercase font-semibold !font-mono"
								>
									{format(parseISO(match.date), 'd MMM', { locale: nl })}
								</time>
							</div>

							{/* Center content */}
							<div className="flex justify-center items-center gap-4 relative h-full">
								{/* Home */}
								<div className="flex flex-1 justify-end items-center gap-2">
									<P className="text-lg text-white !font-bold uppercase hidden lg:block">
										{match.homeContestant.contestantClubName}
									</P>
									<P className="text-lg text-white !font-bold uppercase xs:block lg:hidden">
										{match.homeContestant.contestantCode}
									</P>
									<img
										src={match.homeContestant.contestantLogo || undefined}
										alt={`${match.homeContestant.contestantClubName} logo`}
										className="w-12 h-12"
									/>
								</div>

								{/* Center dash or scores */}
								{hasScores ? (
									<p
										className="text-2xl text-red-500 uppercase font-semibold !font-mono text-center"
										aria-label={`Score: ${match.scores?.home}-${match.scores?.away}`}
									>
										{match.scores?.home}-{match.scores?.away}
									</p>
								) : (
									<p className="text-2xl font-bold text-[#D3D2DD]" aria-label="Match not played yet">
										-
									</p>
								)}

								{/* Channel logo */}
								{!!match.channel && (
									<div className="w-14 absolute top-0 left-1/2 -translate-x-1/2">
										<img
											src={`/images/channel_${match.channel}.png`}
											alt={`${match.channel} channel logo`}
										/>
									</div>
								)}

								{/* Away */}
								<div className="flex flex-1 items-center gap-2">
									<img
										src={match.awayContestant.contestantLogo || undefined}
										alt={`${match.awayContestant.contestantClubName} logo`}
										className="w-12 h-12"
									/>
									<P className="text-lg text-white !font-bold uppercase hidden lg:block">
										{match.awayContestant.contestantClubName}
									</P>
									<P className="text-lg text-white !font-bold uppercase xs:block lg:hidden">
										{match.awayContestant.contestantCode}
									</P>
								</div>
							</div>

							{/* Right side */}
							<div className="py-6 px-4 flex items-center gap-4 justify-end">
								{showButton && (
									<LinkButton href={buttonHref} size="small" variant="primary" className="uppercase">
										{buttonText}
									</LinkButton>
								)}
							</div>
						</article>
					);
				})}
			</div>
		</section>
	));
};

export default SquadMatchesList;
